/**
 * Tags functionality.
 *
 * @module foundation/Tags
 */
import TagFilter from './TagFilter.js';
import PerfectScrollbar from 'perfect-scrollbar/dist/perfect-scrollbar.common.js';

/**
 * Tags Filter component functionality.
 *
 * @requires bootstrap/collapse
 * @requires lodash
 * @requires perfect-scrollbar
 * @extends module:foundation/Tags.TagFilter
 * @memberof module:foundation/Tags
 * @version 1.0.0
 * @author Rocco Janse <rocco.janse@valtech.nl>
 */
class ImportsFilter extends TagFilter {
    /**
     * Upgrades DOM element, gathers querystring data and initializes collapses (for mobile) and filters.
     * @param {DOMElement} element DOM element to upgrade.
     * @param {object} [repository] Repository to get data from.
     */
    constructor(element, repository = null) {
        super(element, repository);

        // get chassisdrawings input
        this.$chassisDrawingsSearch = $('.js-chassisdrawings-keyword').first();
        this.$chassisDrawingsSearch.on({
            'blur.importsfilter': this.onKeyword.bind(this),
            'keyup.importsfilter': this.onKeywordKeyUp.bind(this),
        });
    }

    init() {
        // dropdown elements
        this.dropdowns = this.getDropdowns();

        // create scrollbars
        for (let dropdown of this.dropdowns) {
            dropdown.$scrollbar = new PerfectScrollbar(dropdown.$el.find('.ps')[0], {
                wheelPropagation: false,
            });
        }

        super.init();

        // update filter with querystring params
        if (this.qsParams) {
            this.update({ SelectedTags: this.qsParams });
        }
    }

    /**
     * Enables filter state.
     * @param {object} data Repository data.
     */
    setEnabledFilters(data) {
        if (typeof data.Filters !== 'undefined') {
            let filtersToEnable = data.Filters;
            for (let filter of filtersToEnable) {
                const $input = this.$element.find('input[id="' + filter.Id + '"]');
                if ($input.length > 0) {
                    if (filter.Count > 0) {
                        $input.prop('disabled', false);
                        $input.parent('div').find('.badge').text(filter.Count);
                        $input.parent('div').removeClass('d-none');
                    } else {
                        $input.parent('div').find('.badge').text(filter.Count);
                        $input.parent('div').addClass('d-none');
                    }
                }
            }
        }
    }

    /**
     * Set checked tags.
     * @param {Object} data Repository data.
     */
    setCheckedTags(data) {
        if (typeof data.SelectedTags !== 'undefined') {
            for (const filter in data.SelectedTags) {
                const $input = this.$element.find(`input[name="${filter}"][value="${data.SelectedTags[filter]}"]`);

                if ($input.length > 0) {
                    //$input.prop('checked', true);
                    $input.attr('checked', 'checked');

                    // open parent collapse
                    $input.parents('.collapse').addClass('show');
                    // activate trigger
                    $input.parents('li.filter').find('.collapse-toggle').addClass('active');
                }
            }
        }
    }

    /**
     * Updates filter state.
     * @param {object} data Repository data.
     */
    update(data) {
        const $filters = this.$element.find('input[type=checkbox], input[type=radio]');

        // disable all tags first
        $filters.prop('disabled', true);
        $filters.parent().find('.badge').text(0);

        // set enabled filters
        this.setEnabledFilters(data);

        // set checked tags
        this.setCheckedTags(data);

        // update scrollbars
        this.updateScrollBars();

        // on updating tags, get all current selected
        $filters.off('click.importsfilter').on('click.importsfilter', (e) => {
            // disable when already selected
            if (typeof $(e.currentTarget).attr('checked') === 'undefined') {
                $(e.currentTarget).prop('checked', true);
                $(e.currentTarget).attr('checked', 'checked');
            } else {
                $(e.currentTarget).removeAttr('checked');
                $(e.currentTarget).prop('checked', false);
            }

            // create selected tags
            this.createSelectedTags();

            // update repository
            this.updateRepository({
                Tags: this.selectedTags,
                SelectedTags: this.selectedTags,
            });
        });
    }

    /**
     * Creates selectedTags for the repository.
     */
    createSelectedTags(keyword = null) {
        this.selectedTags = {};

        if (keyword) {
            this.selectedTags['ChassisSearchKeyword'] = keyword;
        } else {
            if (this.$chassisDrawingsSearch.val()) {
                this.selectedTags['ChassisSearchKeyword'] = this.$chassisDrawingsSearch.val();
            }
        }

        // only get other selected values if no keyword is typed
        if (!keyword) {
            for (let filter of this.$element.find('input[type=checkbox]:checked, input[type=radio]:checked')) {
                if (typeof this.selectedTags[$(filter).attr('name')] === 'undefined') {
                    // create selectedTags as string
                    this.selectedTags[$(filter).attr('name')] = $(filter).val();
                } else if (typeof this.selectedTags[$(filter).attr('name')] === 'string') {
                    // if selectedTags is a string, convert to array and add value
                    const current = this.selectedTags[$(filter).attr('name')];
                    this.selectedTags[$(filter).attr('name')] = [current, $(filter).val()];
                } else {
                    // selectedTags is already an array, add value
                    this.selectedTags[$(filter).attr('name')].push($(filter).val());
                }
            }
        }
    }

    /**
     * Updates scrollbars in drodowns.
     */
    updateScrollBars() {
        for (let dropdown of this.dropdowns) {
            dropdown.$scrollbar.update();
        }
    }

    /**
     * Updates Repository.
     * @param {Object} data Query object.
     */
    updateRepository(data) {
        this.repository.getAllItems(data);
    }

    /**
     * Get all filter elements and their collapsibles and triggers.
     * @returns {array} Array of objects with element, trigger and collapsible.
     */
    getDropdowns() {
        let dropdowns = [];
        for (const dropdown of this.$element.find('.dropdown')) {
            dropdowns.push({
                $el: $(dropdown),
            });
        }
        return dropdowns;
    }

    /**
     * "Submit" keyword input when enter is pressed.
     * @param {Event} e jQueryEvent.
     */
    onKeywordKeyUp(e) {
        if (e.keyCode === 13) {
            this.onKeyword(e);
        }
    }

    /**
     * Add keyword to Repository.
     * @param {Event} e jQuery Event.
     */
    onKeyword(e) {
        const $input = $(e.currentTarget);
        if ($input.val() !== '') {
            this.createSelectedTags($input.val());

            // update repository
            this.updateRepository({
                Tags: this.selectedTags,
                SelectedTags: this.selectedTags,
            });
        }
    }
}

export default ImportsFilter;
